<script setup lang="ts">
import { useMainStore } from '@/core/store/main.ts';
import { storeToRefs } from 'pinia';
import anime, { AnimeTimelineInstance } from 'animejs';
import { Maybe, MaybeNull } from '@/shared/types';
import {
  computed, nextTick,
  ref, Ref, watch,
} from 'vue';
import { useAppConfig } from '@/shared/composables/useAppConfig.ts';
import IconLogo from '@/shared/components/icons/IconLogo.vue';
import { useI18n } from 'vue-i18n';
import {
  AvatarOutlineIcon,
  LockTwoOutlineIcon,
  DocumentOutlineIcon,
  MessageOutlineIcon,
  IconEmailOutlineIcon,
  YouTubeIcon,
  InstagramIcon,
  FacebookIcon,
  TikTokIcon,
  PlayOutlineSemiboldIcon,
  // LinkedInIcon,
} from '@/shared/components/icons';
import { OverflowDirection, useOverflowTransparency } from '@/shared/composables/useOverflowTransparency.ts';
import LeaveFeedbackDot from '@/shared/components/core/LeaveFeedbackReminder/LeaveFeedbackDot.vue';
import { useIonRouter } from '@ionic/vue';

const { t } = useI18n();

const router = useIonRouter();

const {
  sideMenuVisible,
  desktopSlideContainerHeight,
  desktopSlideContainerWidth,
} = storeToRefs(useMainStore());

const { desktopSlideContainerBorderRadiusPx, desktopSlideContainerHeightMarginPx } = useAppConfig();

const darkBgVisible = ref(false);

const animationTimeline: Ref<MaybeNull<AnimeTimelineInstance>> = ref(null);

const menu$: Ref<Maybe<HTMLElement>> = ref();
const darkBg$: Ref<Maybe<HTMLElement>> = ref();

const openMenuAnimation = (): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    animationTimeline.value = anime.timeline({
      complete: () => {
        if (animationTimeline.value?.reversed) {
          reject(false);
        } else {
          resolve(true);
        }

        animationTimeline.value = null;
      },
    });

    animationTimeline.value.add({
      targets: menu$.value,
      translateX: ['100%', '0%'],
      duration: 500,
      easing: 'easeInOutCubic',
    }, 0);

    animationTimeline.value.add({
      targets: darkBg$.value,
      opacity: ['0', '0.8'],
      duration: 500,
      easing: 'easeInOutCubic',
    }, 0);
  });
};

const closeMenuAnimation = (): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    animationTimeline.value = anime.timeline({
      complete: () => {
        if (animationTimeline.value?.reversed) {
          reject(false);
        } else {
          resolve(true);
        }

        animationTimeline.value = null;
      },
    });

    animationTimeline.value.add({
      targets: menu$.value,
      translateX: ['0%', '100%'],
      duration: 500,
      easing: 'easeInOutSine',
    }, 0);

    animationTimeline.value.add({
      targets: darkBg$.value,
      opacity: ['0.8', '0'],
      duration: 500,
      easing: 'easeInOutSine',
    }, 0);
  });
};

watch(() => sideMenuVisible.value, (newVal) => {
  if (newVal) {
    darkBgVisible.value = true;
    openMenuAnimation();
  } else {
    closeMenuAnimation().then(() => {
      darkBgVisible.value = false;
    });
  }
});

const desktopSlideContainerWidthPx = computed(() => {
  return `${desktopSlideContainerWidth.value}px`;
});

const desktopSlideContainerHeightPx = computed(() => {
  return `${desktopSlideContainerHeight.value}px`;
});

const navigationList = computed(() => {
  return [
    {
      id: 'profile',
      title: t('burger-menu.nav_list.profile'),
      icon: AvatarOutlineIcon,
      action: () => {
        sideMenuVisible.value = false;

        router.push({
          name: 'Profile',
        });
      },
    },
    {
      id: 'about',
      title: t('burger-menu.nav_list.about'),
      icon: DocumentOutlineIcon,
      action: () => {
        sideMenuVisible.value = false;

        router.push({
          name: 'AboutUs',
        });
      },
    },
    {
      id: 'privacy_policy',
      title: t('burger-menu.nav_list.privacy_policy'),
      icon: LockTwoOutlineIcon,
      action: () => {
        sideMenuVisible.value = false;

        router.push({
          name: 'PrivacyPolicy',
        });
      },
    },
    {
      id: 'terms_of_use',
      title: t('burger-menu.nav_list.terms_of_use'),
      icon: LockTwoOutlineIcon,
      action: () => {
        sideMenuVisible.value = false;

        router.push({
          name: 'TermsOfUse',
        });
      },
    },
    {
      id: 'feedback',
      title: t('burger-menu.nav_list.feedback'),
      icon: MessageOutlineIcon,
      action: () => {
        sideMenuVisible.value = false;

        router.push({
          name: 'LeaveFeedback',
        });
      },
    },
    {
      id: 'news',
      title: t('burger-menu.nav_list.news'),
      icon: PlayOutlineSemiboldIcon,
      action: () => {
        sideMenuVisible.value = false;

        router.push({
          name: 'News',
        });
      },
    },
    {
      id: 'contuct_us',
      title: t('burger-menu.nav_list.contact_us'),
      icon: IconEmailOutlineIcon,
      action: () => {
        sideMenuVisible.value = false;

        router.push({
          name: 'ContactUs',
        });
      },
    },
  ];
});

const socialList = computed(() => {
  return [
    {
      id: 'youtube',
      title: 'YouTube',
      icon: YouTubeIcon,
      action: () => {
        window.open('https://www.youtube.com/@OhMyBible', '_blank');
      },
    },
    {
      id: 'instagram',
      title: 'Instagram',
      icon: InstagramIcon,
      action: () => {
        window.open('https://www.instagram.com/ohmy.bible', '_blank');
      },
    },
    {
      id: 'facebook',
      title: 'Facebook',
      icon: FacebookIcon,
      action: () => {
        window.open('https://www.facebook.com/OhMy.Bible', '_blank');
      },
    },
    {
      id: 'tiktok',
      title: 'TikTok',
      icon: TikTokIcon,
      action: () => {
        window.open('https://www.tiktok.com/@ohmy.bible', '_blank');
      },
    },
    // {
    //   id: 'linkedin',
    //   title: 'LinkedIn',
    //   icon: LinkedInIcon,
    //   action: () => {
    //   },
    // },
  ];
});

const contentContainer$ = ref();

watch(() => sideMenuVisible.value, (value) => {
  if (value) {
    nextTick(() => {
      applyOverflowTransparency();
    });
  }
});

const {
  maskImage: menuMaskImage,
  applyOverflowTransparency,
} = useOverflowTransparency({
  ref: contentContainer$,
  direction: OverflowDirection.Vertical,
});

</script>

<template>
  <div
    v-show="darkBgVisible"
    class="side-menu"
  >
    <div
      v-show="darkBgVisible"
      ref="darkBg$"
      class="dark-bg"
      @click="sideMenuVisible = false"
    />

    <div
      ref="menu$"
      class="menu"
    >
      <div class="blur-circle-1" />
      <div class="blur-circle-2" />
      <div
        ref="contentContainer$"
        class="content"
      >
        <IconLogo class="burger-logo" />
        <ul class="nav-list">
          <li
            v-for="item in navigationList"
            :key="item.id"
            class="nav-list-item remove-blue-highlight"
            @click="item.action"
          >
            <component
              :is="item.icon"
              class="nav-list-item-icon"
            />
            <div class="nav-list-item-title">
              {{ item.title }}
            </div>

            <div
              v-if="item.id === 'feedback'"
              class="feedback-alert-dot"
            >
              <LeaveFeedbackDot />
            </div>
          </li>
        </ul>
        <div class="socials">
          <div class="socials-title">
            {{ t('burger-menu.socials.title') }}
          </div>
          <div class="socials-list">
            <div
              v-for="item in socialList"
              :key="item.id"
              class="social remove-blue-highlight"
              @click="item.action"
            >
              <component
                :is="item.icon"
                class="social-icon"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.side-menu {
  position: fixed;
  top: 0;
  left: 0%;
  width: 100%;
  height: 100%;
  z-index: 550;
  display: flex;
  justify-content: flex-end;
}

@media all and (min-width: 601px) {
  .side-menu {
    overflow: hidden;

    width: v-bind('desktopSlideContainerWidthPx');
    height: v-bind('desktopSlideContainerHeightPx');
    top: v-bind('desktopSlideContainerHeightMarginPx');
    left: 50%;
    right: auto;
    transform: translateX(-50%);
    border-radius: v-bind('desktopSlideContainerBorderRadiusPx');

    .menu {
      zoom: var(--zoom-for-desktop);
    }
  }
}

.dark-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 1);
  opacity: 0;
}

.menu {
  position: relative;
  width: 87%;
  height: 100%;
  background-color: rgba(18, 18, 18, 1);
  transform: translateX(100%);
  overflow: hidden;

  padding: 83px 50px 68px 50px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;

  will-change: transform;
}

.content {
  overflow: auto;
  mask-image: v-bind(menuMaskImage);
}

.blur-circle-1 {
  position: absolute;
  width: 176px;
  height: 165px;
  background-color: rgba(144, 34, 156, 1);
  border-radius: 50%;
  filter: blur(100px);

  bottom: -50px;
  right: -10px;
  z-index: -1;
}

.blur-circle-2 {
  position: absolute;
  width: 176px;
  height: 165px;
  background-color: rgba(95, 34, 156, 1);
  border-radius: 50%;
  filter: blur(100px);

  top: 213px;
  right: -95px;
  z-index: -1;
}

.burger-logo {
  width: 52px;
  height: auto;
  flex-shrink: 0;
}

.nav-list {
  padding: 50px 0;
  margin: 0;

  li + li {
    margin-top: 26px;
  }
}

.nav-list-item {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  column-gap: 11px;
  cursor: pointer;
  position: relative;
}

.nav-list-item-icon {
  width: 20px;
  height: auto;
  max-height: 20px;
  flex-shrink: 0;

  color: #4B3C67;
}

.nav-list-item-title {
  font-family: var(--fontFamily);
  font-weight: 500;
  font-size: 18px;
  line-height: 100%;
  letter-spacing: 0.38px;
  color: rgba(255, 255, 255, 1);

  white-space: nowrap;
}

.socials-title {
  font-family: var(--fontFamily);
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: rgba(86, 71, 90, 1);
  margin-bottom: 10px;
}

.socials-list {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  column-gap: 24px;
}

.social {
  cursor: pointer;
}

.social-icon {
  color: rgba(201, 170, 255, 0.6);
  width: 24px;
  height: 24px;
}

.feedback-alert-dot {
  position: absolute;
  top: 2px;
  left: 18px;
  transform: translate(-50%, -50%);
  z-index: 10;
}
</style>
